import {get} from 'axios';

/**
 * Coarse environment → host table, kept for the customer widget-snippet path:
 * that snippet is generated by WidgetSetup.jsx and only carries an env LABEL
 * (`window.hcOptions.env`), so there is nothing more precise to go on.
 *
 * It is lossy by construction — one label per environment cannot name a host
 * that varies within that environment. Prefer `resolveEmbedRootDomain()`, which
 * uses the server-resolved host when the page provides one.
 */
const getRootDomain = (env) => {
    switch (env) {
        case 'dev':
            return 'helpcenter-io.test';
        case 'staging':
            return 'helpcenter-io-staging.com';
        default:
            return 'helpcenter.io';
    }
}

/**
 * The host serving `embed.<root domain>/widget/faqs/...`.
 *
 * `window.hcOptions.embedRootDomain` is emitted by our own Blade templates
 * straight from `Site::getRootLevelDomain()` — the same value `routes/web.php`
 * registers the endpoint's `Route::domain('embed.'.$rootDomain)` with, so it
 * cannot disagree with where the route actually lives. It has to win over the
 * env table because two environments resolve hosts that no fixed label can
 * express:
 *
 *  - ephemeral staging deploys (`v2-15-3.helpcenter-io-staging.com`), where
 *    getRootLevelDomain() deliberately returns the full APP_URL host rather
 *    than the bare staging root (pinned by tests/Feature/SiteTest.php);
 *  - `development` served off `try.dev-area.helpcenter.io`.
 *
 * In both, collapsing to a label points the request at a DIFFERENT deployment,
 * whose FAQ keys 404 — leaving the embed on its loading state indefinitely.
 */
const resolveEmbedRootDomain = () => (
    window.hcOptions?.embedRootDomain || getRootDomain(window.hcOptions?.env)
);

const getFAQAnalyticsBootstrapUrl = (sectionId) => {
    return `https://embed.${resolveEmbedRootDomain()}/widget/faqs/${sectionId}/analytics/bootstrap`;
};

/**
 * Where this page reads a FAQ section from.
 *
 * `window.hcOptions.faqDataBasePath` is emitted only by the help center's own
 * Blade templates (the v1 shared FAQ page, the v1 site layout, the v2 article
 * page). It is root-relative on purpose — "same origin" is the whole point, so
 * there is no host, scheme or port to get wrong behind a CDN. The v2 article
 * page rendered inside the customizer preview is on the DASHBOARD's origin, so
 * it emits the preview's own endpoint as the base rather than `/faqs`; either
 * way this appends `/<key>/data`.
 *
 * That split is privacy, not tidiness. The embed host below is a DIFFERENT
 * origin from every page that consumes it: the request carries no session
 * cookie and CORS there grants no credentials, so it can only ever answer as
 * an anonymous visitor. An external embed on a customer's marketing page is
 * public-only by design and keeps that path. A signed-in staff member reading
 * the help center must not be reduced to a guest, so those pages take the
 * same-origin one and their session comes with them.
 */
const faqSectionDataUrl = (sectionId, isPreview, lang) => {
    // The help center's own pages hand over their locale (`hcOptions.lang`);
    // an external embed, whatever its snippet says. One value either way.
    const query = lang ? '?lang=' + encodeURIComponent(lang) : '';

    if (isPreview) {
        return window.initialData.urls.preview + '/' + sectionId;
    }

    const sameOriginBase = window.hcOptions?.faqDataBasePath;
    if (sameOriginBase) {
        return sameOriginBase + '/' + sectionId + '/data' + query;
    }

    return 'https://embed.' + resolveEmbedRootDomain() + '/widget/faqs/' + sectionId + query;
};

/**
 * Sections this page has already read, by data URL.
 *
 * Only the v2 customizer preview ever asks twice: it replaces the article on
 * every live edit and article-enhancements.tsx mounts the section afresh, so
 * without this each edit re-fetched it behind a "FAQ section is loading…"
 * flash. A hit answers synchronously, inside the mount, so the section paints
 * straight back. Page lifetime only, and never for the dashboard modal
 * (`isPreview`), which reopens after the section it previews was edited.
 */
const loadedSections = new Map();

/**
 * Read a FAQ section, then call exactly one of `onSuccess` / `onFailure`.
 *
 * `onFailure` is what lets an embed leave its preloader. Without it a 404 (a
 * section deleted from under an article, or a page that pointed the read at a
 * host the route is not on — the v2 customizer preview once did) or a dropped
 * connection left "FAQ section is loading…" spinning for good, because
 * nothing ever told the component the answer was not coming.
 */
function loadFAQSectionData(sectionId, onSuccess, isPreview, lang, onFailure = () => {}) {
    const dataUrl = faqSectionDataUrl(sectionId, isPreview, lang);
    if (!isPreview && loadedSections.has(dataUrl)) {
        onSuccess(loadedSections.get(dataUrl));
        return;
    }

    const fail = (reason) => {
        console.warn('[helpcenter] FAQ section "' + sectionId + '" could not be loaded.', reason);
        onFailure(reason);
    };

    get(dataUrl).then((resp) => {
        if (resp.status === 200 && resp.data && resp.data.status === 'success') {
            if (!isPreview) {
                loadedSections.set(dataUrl, resp.data);
            }
            onSuccess(resp.data);
        } else {
            fail(resp);
        }
    }, fail);
}

/**
 * What an FAQ section says in place of its content when the help center's IP
 * allow-list refuses the visitor's network (a 403 `SITE_IP_RESTRICTED`), given
 * the `onFailure` reason. The visitor can act on that refusal, so it is named.
 * Any other failure answers null, and the section renders nothing.
 */
const faqRefusalNotice = (reason) => (reason?.response?.data?.code === 'SITE_IP_RESTRICTED'
    ? 'This help center can only be opened from approved networks. If you usually have access, connect to your organization\'s network or VPN and reload the page.'
    : null);

/**
 * A section's title or description, in the language the embed reads it in.
 *
 * `lang` is 'default' on the site's default-language pages and the page's
 * language code everywhere else (FAQSection.jsx maps it). The dashboard stores
 * both fields keyed `default` only (components/FAQSections.jsx), so on a
 * localized page `field[lang]` is missing and the heading used to render
 * empty. A translation wins when there is one; otherwise the section reads
 * the way it does on every other page.
 */
const sectionText = (field, lang) => {
    if (typeof field === 'string') {
        return field;
    }
    if (!field || typeof field !== 'object') {
        return '';
    }

    const translated = field[lang];
    if (typeof translated === 'string' && translated.trim() !== '') {
        return translated;
    }

    return typeof field.default === 'string' ? field.default : '';
};

export { faqRefusalNotice, getFAQAnalyticsBootstrapUrl, getRootDomain, loadFAQSectionData, resolveEmbedRootDomain, sectionText };
